import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('border-b border-border', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    icon?: React.ReactNode;
    /** A control shown in the header just left of the chevron (e.g. "+ Add").
     *  It sits beside the trigger, not inside it, so clicking it never
     *  toggles the section. */
    action?: React.ReactNode;
  }
>(({ className, children, icon, action, ...props }, ref) => (
  <AccordionPrimitive.Header className="relative flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'flex flex-1 items-center justify-between px-5 py-3.5 text-[13px] font-semibold tracking-[-0.01em] text-foreground transition-colors hover:text-foreground/80 [&[data-state=open]>svg]:rotate-180',
        className,
      )}
      {...props}
    >
      <span className="flex items-center gap-2">
        {icon && (
          <span className="flex items-center justify-center w-4 h-4 text-muted-foreground">
            {icon}
          </span>
        )}
        {children}
      </span>
      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
    {action && (
      <div className="absolute right-11 top-1/2 flex -translate-y-1/2 items-center">
        {action}
      </div>
    )}
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn('px-5 pb-4 pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
